import axios from "axios";
import { unauthorizedInterceptors } from "../utilities/functions";
const marketingAPI = axios.create({
  baseURL: `${process.env.REACT_APP_API_BASE_URL}/marketing`,
  headers: {
    "Content-Type": "application/json",
  },
});
unauthorizedInterceptors(marketingAPI);
const setAuthHeader = () => {
  const token = localStorage.getItem("accessToken");
  if (token) {
    marketingAPI.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  }
};

// --- GET cache & de-duplication (prevent duplicate requests) ---
const marketingCache = new Map();
const marketingInFlight = new Map();
const MARKETING_TTL_MS = 2 * 60 * 1000; // 2 minutes

export const clearMarketingCache = () => {
  marketingCache.clear();
  console.log("[Cache] Marketing cache cleared.");
};

const cachedMarketingGet = async (url, params = {}, ttlMs = MARKETING_TTL_MS) => {
  setAuthHeader();
  
  const queryString = new URLSearchParams(params).toString();
  const fullUrl = queryString ? `${url}?${queryString}` : url;
  const key = `GET ${fullUrl}`;
  const now = Date.now();

  const cached = marketingCache.get(key);
  if (cached && now - cached.time < ttlMs) {
    return cached.response;
  }

  if (marketingInFlight.has(key)) {
    return marketingInFlight.get(key);
  }

  const requestPromise = marketingAPI
    .get(url, { params })
    .then((response) => {
      marketingCache.set(key, { response, time: Date.now() });
      marketingInFlight.delete(key);
      return response;
    })
    .catch((err) => {
      marketingInFlight.delete(key);
      throw err;
    });

  marketingInFlight.set(key, requestPromise);
  return requestPromise;
};

// === Type of Offer Endpoints ===
export const getTypeOfOffers = async () => {
  setAuthHeader();
  return await marketingAPI.get("api/type-of-offers/");
};

export const createTypeOfOffer = async (offerData) => {
  setAuthHeader();
  return await marketingAPI.post("api/type-of-offers/", offerData);
};

export const getTypeOfOfferById = async (id) => {
  setAuthHeader();
  return await marketingAPI.get(`api/type-of-offers/${id}/`);
};

// === Form of Offer Endpoints ===
export const getFormOfOffers = async () => {
  setAuthHeader();
  return await marketingAPI.get("api/form-of-offers/");
};

export const createFormOfOffer = async (formData) => {
  setAuthHeader();
  return await marketingAPI.post("api/form-of-offers/", formData);
};

export const getFormOfOfferById = async (id) => {
  setAuthHeader();
  return await marketingAPI.get(`api/form-of-offers/${id}/`);
};

// === Offer Details Endpoints ===
export const getOfferDetails = async () => {
  setAuthHeader();
  return await marketingAPI.get("api/offer-details/");
};

export const createOfferDetail = async (detailData) => {
  setAuthHeader();
  return await marketingAPI.post("api/offer-details/", detailData);
};
export const updateOfferDetail = async (id, detailData) => {
  setAuthHeader();
  return await marketingAPI.put(`api/offer-details/${id}/`, detailData);
};

export const getOfferDetailById = async (id) => {
  setAuthHeader();
  return await marketingAPI.get(`api/offer-details/${id}/`);
};

// === Offer Endpoints ===
export const getOffers = async () => {
  setAuthHeader();
  return await marketingAPI.get("api/offers/");
};

export const createOffer = async (offerData) => {
  setAuthHeader();
  return await marketingAPI.post("api/offers/", offerData);
};

export const updateOffer = async (id, offerData) => {
  setAuthHeader();
  return await marketingAPI.put(`api/offers/${id}/`, offerData);
};

export const getOfferById = async (id) => {
  setAuthHeader();
  return await marketingAPI.get(`api/offers/${id}/`);
};

export const deleteOffer = async (id) => {
  setAuthHeader();
  return await marketingAPI.delete(`api/offers/${id}/`);
};

// === Outside Envelope Endpoints ===
export const getOutsideEnvelopes = async () => {
  setAuthHeader();
  return await marketingAPI.get("api/outside-envelopes/");
};

export const createOutsideEnvelope = async (envelopeData) => {
  setAuthHeader();
  return await marketingAPI.post("api/outside-envelopes/", envelopeData);
};

export const getOutsideEnvelopeById = async (id) => {
  setAuthHeader();
  return await marketingAPI.get(`api/outside-envelopes/${id}/`);
};

// === Marketing Files Endpoints ===
export const getMarketingFiles = async () => {
  setAuthHeader();
  return await marketingAPI.get("api/marketing-files/");
};

export const createMarketingFile = async (fileData) => {
  setAuthHeader();
  return await marketingAPI.post("api/marketing-files/", fileData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const cloneMarketingFile = async (cloneData) => {
  setAuthHeader();
  return await marketingAPI.post("api/marketing-files/clone-from-existing/", cloneData);
};
export const updateMarketingFile = async (id, fileData) => {
  setAuthHeader();
  return await marketingAPI.put(`api/marketing-files/${id}/`, fileData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const getMarketingFileById = async (id) => {
  setAuthHeader();
  return await marketingAPI.get(`api/marketing-files/${id}/`);
};

export const deleteMarketingFile = async (id) => {
  setAuthHeader();
  return await marketingAPI.delete(`api/marketing-files/${id}/`);
};
export const getMarketingFilesByOfferId = async (offerId) => {
  setAuthHeader();
  return await marketingAPI.get(`api/marketing-files/by-offer/`, {
    params: { offer_id: offerId },
  });
};

// CACHED - heavily used, prevents duplicate calls
export const getMarketingFilesByBrand = async (brandId) => {
  return await cachedMarketingGet(`api/marketing-files/by-brand/`, { brand_id: brandId });
};

export const getMarketingFilesNotLinkedToMarketing = async (
  typeId = null,
  clientIds = []
) => {
  setAuthHeader();
  const params = new URLSearchParams();

  if (typeId) {
    params.append("type_id", typeId);
  }
  if (clientIds && clientIds.length > 0) {
    clientIds.forEach((id) => params.append("client_ids[]", id));
  }

  return await marketingAPI.get(`api/marketing-files/not-linked/?${params.toString()}`);
};

export const getMarketingFileTypes = async () => {
  setAuthHeader();
  return await marketingAPI.get("api/marketing-file-types/");
};
export const createMarketingFileType = async (typeData) => {
  setAuthHeader();
  return await marketingAPI.post("api/marketing-file-types/", typeData);
};

export const getMarketingList = async (clientids, page = 1, pageSize = 20) => {
  setAuthHeader();
  const params = new URLSearchParams();

  if (clientids && clientids.length > 0) {
    clientids.forEach((id) => params.append("client_ids[]", id));
  }

  params.append("page", page);
  params.append("page_size", pageSize);

  return await marketingAPI.get(`/api/marketing-list/?${params.toString()}`);
};

// === payment details endpoints ===
export const getPaymentDetails = async () => {
  setAuthHeader();
  return await marketingAPI.get("api/payment-details/");
};

export const createPaymentDetail = async (paymentData) => {
  setAuthHeader();
  return await marketingAPI.post("api/payment-details/", paymentData);
};

export const getPaymentDetailById = async (id) => {
  setAuthHeader();
  return await marketingAPI.get(`api/payment-details/${id}/`);
};
// === Offer Pricing Endpoints ===
export const getMarketingSKUs = async (clientids) => {
  setAuthHeader();
  const params = new URLSearchParams();

  if (clientids && clientids.length > 0) {
    clientids.forEach((id) => params.append("client_ids[]", id));
  } else {
    params.append("all", true);
  }

  return await marketingAPI.get(`/api/marketing-skus/?${params.toString()}`);
};

export const getMarketingSKUsByBrand = async (brandId) => {
  setAuthHeader();
  const params = new URLSearchParams();

  if (brandId) {
    params.append("brand_id", brandId);
  }

  return await marketingAPI.get(`/api/marketing-skus/by_brand/?${params.toString()}`);
};

export const createMarketingSKU = async (pricingData) => {
  setAuthHeader();
  return await marketingAPI.post("api/marketing-skus/", pricingData);
};

export const updateMarketingSKU = async (id, pricingData) => {
  setAuthHeader();
  return await marketingAPI.put(`api/marketing-skus/${id}/`, pricingData);
};

export const getMarketingSKUById = async (id) => {
  setAuthHeader();
  return await marketingAPI.get(`api/marketing-skus/${id}/`);
};

export const deleteMarketingSKU = async (id) => {
  setAuthHeader();
  return await marketingAPI.delete(`api/marketing-skus/${id}/`);
};

// === Offer Item Endpoints ===
export const getDependentMarketingSKUs = async () => {
  setAuthHeader();
  return await marketingAPI.get("api/marketing-dependent-skus/");
};

export const createDependentMarketingSKU = async (itemData) => {
  setAuthHeader();
  return await marketingAPI.post("api/marketing-dependent-skus/", itemData);
};

export const updateDependentMarketingSKU = async (id, itemData) => {
  setAuthHeader();
  return await marketingAPI.put(`api/marketing-dependent-skus/${id}/`, itemData);
};

export const getDependentMarketingSKUById = async (id) => {
  setAuthHeader();
  return await marketingAPI.get(`api/marketing-dependent-skus/${id}/`);
};

export const deleteDependentMarketingSKU = async (id) => {
  setAuthHeader();
  return await marketingAPI.delete(`api/marketing-dependent-skus/${id}/`);
};

export const getMarketingsByProductVariation = async (product_varaition_id) => {
  setAuthHeader();
  return await marketingAPI.get(`api/marketings-by-product-variation/${product_varaition_id}/`);
};

export const getNextOfferNumber = async (productId) => {
  setAuthHeader();
  return await marketingAPI.get(`api/offers/next-offer-number/`, {
    params: { product_id: productId },
  });
};
// CACHED - heavily used, prevents duplicate calls
export const getOffersByBrand = async (brandId) => {
  return await cachedMarketingGet("api/offers/by-brand/", { brand_id: brandId });
};
